'use client';

import { useEffect } from 'react';

// Cookie cleanup timing constants
const INITIAL_CLEANUP_DELAY_MS = 5000;
const PERIODIC_CLEANUP_INTERVAL_MS = 30000;

/**
 * Cookies from ad/tracking vendors that accumulate and cause "Request Header Too Large" errors.
 * Using a blacklist so new first-party cookies work by default.
 */
const VENDOR_COOKIE_PATTERNS = [
  // Criteo
  /^cto_/,
  /^criteo/,

  // Google Ads / DoubleClick
  /^_gcl_/,
  /^__gads$/,
  /^__gpi$/,
  /^DSID$/,
  /^IDE$/,
  /^__gfp_/,
  /^test_cookie$/,

  // Amazon Ads
  /^ad-id$/,
  /^ad-privacy$/,
  /^amzn/,

  // Prebid
  /^prebid/,
  /^_pb/,
  /^hb_/,

  // Quantcast
  /^__qc/,
  /^mc$/,

  // Parsely
  /^_parsely/,

  // Facebook/Meta
  /^_fbp$/,
  /^_fbc$/,

  // TikTok
  /^_tt_/,

  // Pinterest
  /^_pinterest/,
  /^_pin_/,

  // LinkedIn
  /^li_/,
  /^bcookie$/,
  /^bscookie$/,

  // Twitter/X
  /^_twc/,
  /^muc_ads$/,

  // TradeDesk
  /^TDID$/,
  /^TDCPM$/,

  // LiveRamp
  /^_lr_/,
  /^rlas3$/,

  // Bombora
  /^_bm_/,

  // Other ad networks
  /^_uetsid/,
  /^_uetvid/,
  /^tuuid/,
  /^uuid2$/,
  /^c$/,
  /^anj$/,
];

/**
 * Checks if a cookie is from a vendor that should be cleaned up.
 */
const isVendorCookie = (name: string): boolean => {
  return VENDOR_COOKIE_PATTERNS.some((pattern) => pattern.test(name));
};

/**
 * Deletes a cookie by name, trying multiple path/domain combinations.
 */
const deleteCookie = (name: string): void => {
  const hostname = window.location.hostname;
  const paths = ['/', ''];
  const domains = [hostname, `.${hostname}`, ''];
  const expiry = 'Thu, 01 Jan 1970 00:00:00 GMT';

  for (const path of paths) {
    for (const domain of domains) {
      const domainPart = domain ? `; domain=${domain}` : '';
      // biome-ignore lint/suspicious/noDocumentCookie: Direct cookie manipulation required for deletion
      document.cookie = `${name}=; expires=${expiry}; path=${path}${domainPart}`;
    }
  }
};

/**
 * Cleans up vendor cookies in dev environments.
 * Only removes known ad/tracking cookies, leaving all other cookies intact.
 */
const cleanupVendorCookies = (): void => {
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const name = cookie.split('=')[0]?.trim();
    if (name && isVendorCookie(name)) {
      deleteCookie(name);
    }
  }
};

/**
 * Component that cleans up vendor cookies in non-production environments.
 * Uses a blacklist approach so new first-party cookies work by default.
 */
export const DevCookieCleanup = () => {
  useEffect(() => {
    // Initial cleanup after scripts have had time to initialize
    const initialCleanup = setTimeout(() => {
      cleanupVendorCookies();
    }, INITIAL_CLEANUP_DELAY_MS);

    // Periodic cleanup every 30 seconds to catch new cookies
    const periodicCleanup = setInterval(() => {
      cleanupVendorCookies();
    }, PERIODIC_CLEANUP_INTERVAL_MS);

    return () => {
      clearTimeout(initialCleanup);
      clearInterval(periodicCleanup);
    };
  }, []);

  return null;
};
